import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import { Group, UsersService } from "@/generated";
import { setActiveGroupId } from "@/utils/activeGroup";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { SpotToast } from "../SpotToast";
import { useSpotUser } from "@/utils/spotUser";
import { defaultPickerColourId } from "@/utils/colourTokens";
import { GroupForm } from "../Form/Forms/GroupForm";
import BaseDialog from "./BaseDialog";

interface IFormData {
  name: string;
  description: string;
  color: string;
}

export const GroupModal = ({
  selectedGroup,
  isOpen,
  isEdit,
  onOpenChange,
}: {
  selectedGroup?: Group;
  isOpen: boolean;
  isEdit?: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const { spotUser, spotGroup } = useSpotUser();
  const { control, handleSubmit, formState } = useForm<IFormData>({
    mode: "onChange",
    defaultValues: {
      name: selectedGroup?.name || "",
      description: selectedGroup?.description || "",
      color: defaultPickerColourId(
        selectedGroup?.settings?.color as string | undefined
      ),
    },
  });

  const { errors } = formState;

  const editGroupMutation = useMutation({
    mutationFn: UsersService.editGroup,
    onSuccess: (res) => {
      SpotToast.success("Updated successfully");
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.GROUP] });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.GROUPS] });
    },
    onError: () => {
      SpotToast.error(
        "Could not edit workspace details, please try again later."
      );
    },
    onSettled: () => {
      onOpenChange(false);
    },
  });

  const updateMeMutation = useMutation({
    mutationFn: UsersService.updateMe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.USER] });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.GROUP] });
    },
    onError: () => {
      SpotToast.error(
        "There was an error redirecting to the new workspace. Try again manually"
      );
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: UsersService.createGroup,
    onSuccess: (res) => {
      // The backend create_group endpoint already adds the current user as admin
      // On success we add the user to the group they created. The group is updated independently
      // of the user addition failing (if it fails at least the group is created)
      setActiveGroupId(res.data.group.id);
      updateMeMutation.mutate({
        body: {
          settings: { ...spotUser?.settings, defaultGroup: res.data.group.id },
        },
      });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.GROUPS],
      });
    },
    onError: () => {
      SpotToast.error(
        "Could not edit workspace details, please try again later."
      );
    },
    onSettled: () => {
      onOpenChange(false);
    },
  });

  const handleFormSubmit = (data: IFormData) => {
    if (isEdit) {
      editGroupMutation.mutate({
        path: {
          group_id: selectedGroup!.id,
        },
        body: {
          name: data.name,
          description: data.description,
          settings: {
            color: data.color,
          },
        },
      });
    } else {
      createGroupMutation.mutate({
        body: {
          name: data.name,
          description: data.description,
          settings: {
            color: data.color,
          },
        },
      });
    }
  };

  const isSubmitting =
    editGroupMutation.status === "pending" ||
    createGroupMutation.status === "pending";

  const footerActions = [
    {
      label: "Cancel",
      props: { variant: "tertiary", onClick: () => onOpenChange(false) },
    },
    {
      label: isEdit ? "Save" : "Create",
      props: {
        loading: isSubmitting,
        loadingText: isEdit ? "Saving..." : "Creating...",
        type: "submit",
        onClick: handleSubmit(handleFormSubmit),
        disabled: isSubmitting || !formState.isValid,
      },
    },
  ];

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={isEdit ? "Edit workspace" : "New workspace"}
      footerActions={footerActions}
    >
      <GroupForm
        control={control}
        handleSubmit={handleSubmit(handleFormSubmit)}
        errors={errors}
      />
    </BaseDialog>
  );
};
